import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useTitle } from "../../hooks/useTitle";
import useHttp from "../../hooks/use-http";
import toast from "react-hot-toast";
import Spinner from "../Spinner";

const copyToClipboard = (textToCopy) => {
  navigator.clipboard
    .writeText(textToCopy)
    .then(() => {
      toast.success("Copied to clipboard!");
    })
    .catch((err) => {
      toast.underline("Unable to copy to clipboard. Please try again.");
      console.error("Copy to clipboard failed:", err);
    });
};

const PayByTransfer = () => {
  const { transactionInfo } = useOutletContext();
  const [transferInfo, setTransferInfo] = useState({});
  const [time, setTime] = useState(null);
  const [url, setUrl] = useState("");
  const [error] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [isExpired, setIsExpired] = useState(false);

  const navigate = useNavigate();
  const { isLoading: detailsLoading, sendRequest: initiateTransfer } =
    useHttp();
  const { sendRequest: checkStatus, isLoading: checkLoading } = useHttp();
  const { sendRequest: fetchUrl } = useHttp();
  const { sendRequest: fetchStatus } = useHttp();
  const [isLoading, setIsLoading] = useState(false);

  const { id } = useParams();
  useTitle("Transfer");

  const isTransactionPending = useRef(false);
  const transferCalled = useRef(false);

  const getUrl = useCallback((data) => {
    setUrl(data?.data?.returnUrl);
  }, []);

  useEffect(() => {
    fetchUrl(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/paybytransfer/status/${id}`,
        headers: { "Content-Type": "application/json" },
      },
      getUrl
    );
  }, [getUrl, fetchUrl, id]);

  const convertStringToTime = (timeString) => {
    if (!timeString) return null;
    const [timeVal, period] = timeString.split(" ");
    if (!timeVal || !period) return null;

    const [hours, minutes] = timeVal.split(":");
    if (!hours || !minutes) return null;

    let newHours = parseInt(hours);
    if (period === "PM" && newHours !== 12) {
      newHours += 12;
    }

    const timeObject = new Date();
    timeObject.setHours(newHours, parseInt(minutes), 0, 0);
    return timeObject;
  };

  const calculateTimeDifference = useCallback(() => {
    const currentTime = new Date();
    const expirationTime = convertStringToTime(transferInfo?.data?.time);

    if (expirationTime) {
      const timeDiff = expirationTime - currentTime;
      if (timeDiff > 0) {
        setTime(formatTime(timeDiff));
      } else {
        setTime("00:00");
      }
    }
  }, [transferInfo?.data?.time]);

  const formatTime = (timeInMilliseconds) => {
    const minutes = Math.floor(timeInMilliseconds / (1000 * 60));
    const seconds = Math.floor((timeInMilliseconds % (1000 * 60)) / 1000);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (time === "00:00") {
      setIsExpired(true);
      setStatusMessage(`Payment Window Expired. If debited, please contact support with Reference ID: ${id}`);
      const redirectTimer = setTimeout(() => {
        if (url) {
          window.location.href = url;
        }
      }, 15000);
      return () => clearTimeout(redirectTimer);
    }
  }, [time, url, id]);

  const handleCopyToClipboard = (textToCopy) => {
    copyToClipboard(textToCopy);
  };

  useEffect(() => {
    calculateTimeDifference();
    const interval = setInterval(() => {
      calculateTimeDifference();
    }, 1000);
    return () => clearInterval(interval);
  }, [calculateTimeDifference]);

  const getTransferInfo = (data) => {
    setTransferInfo(data);
  };

  const transferPayment = useCallback(() => {
    initiateTransfer(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/payByTransfer/initialize/${id}`,
        headers: { "Content-Type": "application/json" },
      },
      getTransferInfo
    );
  }, [initiateTransfer, id]);

  useEffect(() => {
    if (transferCalled.current) return;
    transferCalled.current = true;
    transferPayment();
  }, [transferPayment]);

  const getStatus = () => {
    checkStatus(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/payByTransfer/status/${id}`,
        headers: {
          "Content-Type": "application/json",
        },
      },
      confirmStatus
    );
  };

  const confirmStatus = (data) => {
    const status = data?.data?.status;
    if (status === "PENDING" || status === "Account detail not found") {
      setIsLoading(true);
      setStatusMessage("Checking with Hope Bank... Payment not seen yet due to network delays. We recheck every 30 seconds; please do not retry transfer.");
      if (!isTransactionPending.current) {
        isTransactionPending.current = true;
        toast.success("Transaction is pending");
      }
      setTimeout(getStatus, 30000);
    } else if (status) {
      setIsLoading(false);
      navigate(`/transfer/${id}/payment-confirmation/${id}`);
    } else {
      setIsLoading(true);
      setTimeout(getStatus, 30000);
    }
  };

  const [isDisabled, setIsDisabled] = useState(false);

  const getStatusInBackground = useCallback(() => {
    fetchStatus(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/payByTransfer/status/${id}`,
        headers: {
          "Content-Type": "application/json",
        },
      },
      (data) => {
        const status = data?.data?.status;
        if (status === "PENDING" || status === "Account detail not found") {
          setStatusMessage("Checking with Hope Bank... Payment not seen yet due to network delays. We recheck every 30 seconds; please do not retry transfer.");
          setTimeout(getStatusInBackground, 30000);
        } else if (status) {
          navigate(`/transfer/${id}/payment-confirmation/${id}`);
        } else {
          setTimeout(getStatusInBackground, 30000);
        }
      }
    );
  }, [fetchStatus, id, navigate]);

  useEffect(() => {
    const timerVal = setTimeout(() => {
      getStatusInBackground();
    }, 30000);
    return () => clearTimeout(timerVal);
  }, [getStatusInBackground]);

  const currentCurrency = transactionInfo?.data?.currency || "NGN";
  const formattedCurrencySymbol = currentCurrency === "NGN" ? "₦" : currentCurrency;
  const shoppingCartAmount = transactionInfo?.data?.amount || 0;
  const processingFeeAmount = transactionInfo?.data?.fee || 0;
  const totalAmount = shoppingCartAmount + processingFeeAmount;

  if (detailsLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 font-semibold py-20 px-4">
        {error}
      </div>
    );
  }

  return (
    <div className="w-full max-w-[560px] mx-auto py-2">
      <div className="text-center px-4 mb-4">
        <h3 className="text-lg font-bold text-slate-800 leading-snug mb-1">
          Proceed to your bank app to complete transaction
        </h3>
        <p className="text-xs font-semibold text-amber-600 leading-relaxed max-w-[440px] mx-auto">
          Transfer the exact amount. Do not save or reuse this account details.
        </p>
      </div>

      <div className="space-y-6">
        {/* Account Details Box */}
        <div className="bg-[#f0f9ff]/70 p-6 space-y-4">
          <div className="flex justify-between items-center py-1 border-b border-[#d0ecfa]/55">
            <div>
              <p className="text-[10px] font-extrabold text-[#828282] uppercase tracking-wider">Amount</p>
              <h5 className="text-base font-bold text-slate-800 mt-1">
                {formattedCurrencySymbol}{totalAmount.toLocaleString()}
              </h5>
            </div>
            <button
              onClick={() => handleCopyToClipboard(totalAmount)}
              className="p-2 hover:bg-sky-50 rounded-lg text-sky-600 active:scale-95 transition-all"
              title="Copy Amount"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          </div>

          <div className="flex justify-between items-center py-1 border-b border-[#d0ecfa]/55">
            <div>
              <p className="text-[10px] font-extrabold text-[#828282] uppercase tracking-wider">Account Number</p>
              <h5 className="text-base font-bold text-slate-800 tracking-wider mt-1">
                {transferInfo?.data?.accountNumber || "----------"}
              </h5>
            </div>
            <button
              onClick={() => handleCopyToClipboard(transferInfo?.data?.accountNumber || "")}
              disabled={!transferInfo?.data?.accountNumber}
              className="p-2 hover:bg-sky-50 rounded-lg text-sky-600 active:scale-95 transition-all disabled:opacity-50"
              title="Copy Account Number"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          </div>

          <div className="py-1 border-b border-[#d0ecfa]/55">
            <p className="text-[10px] font-extrabold text-[#828282] uppercase tracking-wider">Bank Name</p>
            <h5 className="text-base font-bold text-slate-800 mt-1">
              {transferInfo?.data?.bankName || "Loading..."}
            </h5>
          </div>

          <div className="py-1">
            <p className="text-[10px] font-extrabold text-[#828282] uppercase tracking-wider">Beneficiary</p>
            <h5 className="text-base font-bold text-slate-800 mt-1">
              {transferInfo?.data?.merchantName || "PAYARENA"}
            </h5>
          </div>
        </div>

        {/* Warning / Timeout Text */}
        <div className="text-center">
          <p className="text-xs font-semibold text-black leading-relaxed font-mono">
            This temporary <b>{transferInfo?.data?.bankName || "Hope Bank"}</b> account expires in <span className="font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded border border-red-100">{time || "--:--"}</span>
          </p>
        </div>

        {statusMessage && (
          <div className={`p-4 rounded-lg text-xs text-center leading-relaxed font-semibold max-w-[480px] mx-auto ${isExpired
            ? "bg-red-50 border border-red-200 text-red-700"
            : "bg-orange-50 border border-orange-200 text-orange-700 animate-pulse"
            }`}>
            {statusMessage}
          </div>
        )}

        {/* Action Button */}
        <div className="pt-2">
          <button
            className="w-full h-[54px] bg-[#00aeef] hover:bg-[#0096ce] active:scale-[0.99] text-white rounded-lg font-bold text-base cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            onClick={() => {
              getStatus();
              setIsDisabled(true);
            }}
            disabled={isDisabled || isLoading || checkLoading || isExpired}
          >
            {isLoading || checkLoading ? (
              <Spinner />
            ) : (
              "I've sent the money"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayByTransfer;
